// audience-list-adapter.tsx — the audience picker adapter, SPLIT OUT of
// `campaign-state-bridge.tsx`.
//
// Behaviour is unchanged and `campaign-state-bridge` re-exports it, so every
// existing import site is untouched. The split is about the MODULE GRAPH:
// `campaign-state-bridge` also hosts `ManagementViewAdapter`, which statically
// imports `CampaignManagementView` -> … -> `chat-task-list.tsx`, and that file
// calls `window.addEventListener` at MODULE SCOPE. Jest here is
// `testEnvironment: 'node'`, so merely importing the bridge from a component
// that a pure test transitively reaches throws `ReferenceError: window is not
// defined` and takes the whole suite down before a single assertion runs —
// which is exactly what happened when `ScheduledCampaignEditModal` (reached
// from `sequence-detail.tsx`) imported the bridge for the picker alone.
//
// So: anything that needs ONLY the audience picker imports this file, and pays
// for `BulkAudiencesList` but not for the entire legacy management view.
//
// The adapter's own contract is the bridge's: snapshot the prior legacy flags on
// mount, set the mode this surface needs, RESTORE the prior values on unmount.
// That is what keeps the surfaces mode-isolated — opening the wizard's audience
// step never leaves the list's own Audiences tab flipped into checkbox-selection
// mode (the audience checkbox-bleed bug).

import React from 'react';

import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import {
  setActiveBulkView,
  setActiveTab,
  type BulkView,
  type BulkDashboardTab,
} from '@common/reducers/CampaignReducer';
import type { CommonRootState } from '@common/types/common-root-state-type';

import BulkAudiencesList from '../bt-bulk-dashboard/audiences/bulk-audiences-list';

// The audience list's mode is chosen by `BulkAudiencesList` /
// `BulkAudienceSelectRow` from `activeBulkView`:
//   • 'campaignManagementView' → management UI: NO checkboxes, row-click opens
//     the board (create / edit / duplicate / delete).
//   • anything else (e.g. 'newCampaignView') → selection UI: checkbox rows that
//     toggle the campaign selection.
// We expose exactly these two intents and map them to the legacy flag here, so
// no redesign surface ever reaches for the raw flag.
export type AudienceListMode = 'management' | 'selection';

export const BULK_VIEW_FOR_MODE: Record<AudienceListMode, BulkView> = {
  management: 'campaignManagementView',
  selection: 'newCampaignView',
};

interface AudienceListAdapterProps {
  mode: AudienceListMode;
}

// Renders the REAL `BulkAudiencesList` in the requested mode, owning the legacy
// flag lifecycle: set on mount, restore the prior values on unmount. Two
// instances of this adapter (list's Audiences tab in 'management', wizard's
// audience step in 'selection') therefore cannot bleed into each other.
export const AudienceListAdapter: React.FC<AudienceListAdapterProps> = ({ mode }) => {
  const dispatch = useAppDispatch();
  const prevBulkView = useAppSelector((s: CommonRootState) => s.CampaignReducer.activeBulkView);
  const prevTab = useAppSelector((s: CommonRootState) => s.CampaignReducer.activeTab);
  // Snapshot the prior values ONCE on mount (a ref, so re-renders driven by our
  // own writes don't re-capture the value we just set).
  const restoreRef = React.useRef<{ bulkView: BulkView; tab: BulkDashboardTab }>({
    bulkView: prevBulkView,
    tab: prevTab,
  });

  React.useEffect(() => {
    const restore = restoreRef.current;
    dispatch(setActiveBulkView(BULK_VIEW_FOR_MODE[mode]));
    dispatch(setActiveTab('audiences'));
    return () => {
      dispatch(setActiveBulkView(restore.bulkView));
      dispatch(setActiveTab(restore.tab));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, mode]);

  return <BulkAudiencesList />;
};
